/**
 * Migration helpers between legacy 5-boolean capabilities, retired v1.0 keys,
 * and the normative v1.1 allowlist.
 * Contract: docs/DIAGNOSTIC_CAPABILITY_REGISTRY_V1.md §3
 */

import {
  DIAGNOSTIC_CAPABILITY_KEYS,
  DIAGNOSTIC_LAB_CAPABILITY_KEYS,
} from "./diagnosticCapabilities";

const ALLOWED = new Set(DIAGNOSTIC_CAPABILITY_KEYS);

const V10_EXPAND = Object.freeze({
  xray: ["xray_chest", "xray_extremity"],
  ultrasound: ["us_soft_tissue"],
  ct_scan: ["ct_head", "ct_abdomen_pelvis"],
});

/**
 * Filter to current allowlist, dedupe, stable order by DIAGNOSTIC_CAPABILITY_KEYS.
 * @param {unknown} keys
 * @returns {string[]}
 */
export function normalizeDiagnosticCapabilities(keys) {
  if (!Array.isArray(keys)) return [];
  const set = new Set();
  for (const key of keys) {
    if (typeof key === "string" && ALLOWED.has(key)) set.add(key);
  }
  return DIAGNOSTIC_CAPABILITY_KEYS.filter((k) => set.has(k));
}

/**
 * Seed v1.1 keys from legacy Status Board booleans.
 * @param {object | null | undefined} capabilities
 * @returns {string[]}
 */
export function legacyBooleansToV11Keys(capabilities = {}) {
  const caps = capabilities || {};
  const keys = [];
  if (caps.xray === true) {
    keys.push("xray_chest", "xray_extremity");
  }
  if (caps.ultrasound === true) {
    keys.push("us_soft_tissue");
  }
  if (caps.ct === true) {
    keys.push("ct_head", "ct_abdomen_pelvis");
  }
  if (caps.mri === true) {
    keys.push("mri");
  }
  if (caps.lab === true) {
    keys.push("cbc", "bmp", "urinalysis");
  }
  return normalizeDiagnosticCapabilities(keys);
}

/**
 * Expand retired v1.0 keys to v1.1; drop retired keys from the result.
 * Does not auto-add splinting from fracture_management.
 * @param {unknown} keys
 * @returns {string[]}
 */
export function expandV10KeysToV11(keys) {
  if (!Array.isArray(keys)) return [];
  const out = [];
  for (const key of keys) {
    if (typeof key !== "string") continue;
    if (V10_EXPAND[key]) {
      out.push(...V10_EXPAND[key]);
    } else if (ALLOWED.has(key)) {
      out.push(key);
    }
  }
  return normalizeDiagnosticCapabilities(out);
}

/**
 * Derive legacy 5-boolean capabilities from a v1.1 key list (score / map chips).
 * @param {unknown} keys
 * @returns {{ lab: boolean, xray: boolean, ultrasound: boolean, ct: boolean, mri: boolean }}
 */
export function deriveLegacyCapabilitiesFromV11(keys) {
  const set = new Set(normalizeDiagnosticCapabilities(keys));
  const hasLab = DIAGNOSTIC_LAB_CAPABILITY_KEYS.some((k) => set.has(k));
  return {
    lab: hasLab,
    xray: set.has("xray_chest") || set.has("xray_extremity"),
    ultrasound:
      set.has("us_soft_tissue") ||
      set.has("us_pelvic") ||
      set.has("us_vascular_doppler") ||
      set.has("echo"),
    ct:
      set.has("ct_head") ||
      set.has("ct_abdomen_pelvis") ||
      set.has("cta"),
    mri: set.has("mri"),
  };
}

/**
 * Resolve the keys to show/edit on Status Board load.
 * Prefer non-empty diagnosticCapabilities (after v1.0 expansion); else seed from legacy booleans.
 * @param {object | null | undefined} locationDoc
 * @returns {string[]}
 */
export function resolveAttestedKeysForForm(locationDoc) {
  const stored = locationDoc?.diagnosticCapabilities;
  if (Array.isArray(stored) && stored.length > 0) {
    return expandV10KeysToV11(stored);
  }
  const legacy =
    locationDoc?.capabilities ||
    ({
      lab: locationDoc?.lab,
      xray: locationDoc?.xray,
      ultrasound: locationDoc?.ultrasound,
      ct: locationDoc?.ct,
      mri: locationDoc?.mri,
    });
  return legacyBooleansToV11Keys(legacy);
}

const diagnosticCapabilityMigration = {
  normalizeDiagnosticCapabilities,
  legacyBooleansToV11Keys,
  expandV10KeysToV11,
  deriveLegacyCapabilitiesFromV11,
  resolveAttestedKeysForForm,
};

export default diagnosticCapabilityMigration;
